import { type AnnualPlanTeamMember } from '../../../models/AnnualPlanTeamMember'
import { Box, Card, type SxProps, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { ProfileImage } from '../../customComponents/user/ProfileImage'
import { LabelWithToolTip } from '../../customComponents/LabelWithTooltip'
import { AnnualPlanRole } from '../../../models/AnnualPlanRole'
import { AnnualPlanningTeamModal } from './AnnualPlanningTeamModal'
import { EditOutlined } from '@mui/icons-material'
import { InactivePersonIcon } from '../../../assets/svgs/InactivePersonIcon'

interface Props {
  annualPlanId: number
  readonly: boolean
  team: AnnualPlanTeamMember[]
  implementationPhase?: boolean
  onTeamChange: () => Promise<void>
}

export const AnnualPlanTeamCard = (props: Props): JSX.Element => {
  const purpleBackground: string = 'var(--purple-500)'
  const blueBackground: string = 'var(--blue-300)'
  const greenBackground: string = 'var(--green-500)'
  const [planAdministrators, setPlanAdministrators] = useState<AnnualPlanTeamMember[]>([])
  const [planContributors, setPlanContributors] = useState<AnnualPlanTeamMember[]>([])
  const [planReviewers, setPlanReviewers] = useState<AnnualPlanTeamMember[]>([])
  const [showEditModal, setShowEditModal] = useState(false)

  const setData = async (): Promise<void> => {
    const team = props.team
    const admins: AnnualPlanTeamMember[] = []
    const contributors: AnnualPlanTeamMember[] = []
    const reviewers: AnnualPlanTeamMember[] = []

    team.forEach(tm => {
      if (tm.annualPlanRole === AnnualPlanRole.PlanAdministrator) {
        admins.push(tm)
      } else if (tm.annualPlanRole === AnnualPlanRole.PlanContributor) {
        contributors.push(tm)
      } else if (tm.annualPlanRole === AnnualPlanRole.PlanReviewer) {
        reviewers.push(tm)
      }
    })

    setPlanAdministrators(admins)
    setPlanContributors(contributors)
    setPlanReviewers(reviewers)
  }

  useEffect(() => {
    void setData()
  }, [props.annualPlanId, props.team])

  const closeModal = (): void => {
    setShowEditModal(false)
  }

  const teamSubmit = async (): Promise<void> => {
    await props.onTeamChange()
    setShowEditModal(false)
  }

  const activeStyle: SxProps = { display: 'flex', alignItems: 'center', my: '1em', ml: '15%', width: '75%' }
  const inactiveStyle: SxProps = { ...activeStyle, height: '50px', backgroundColor: 'var(--grey-300)', borderRadius: '8px' }
  return <>
    <Card key='annualPlanCard' sx={{ backgroundColor: 'rgb(from var(--grey-100) r g b)', minHeight: '3em', border: '1px solid var(--border)', p: '1em' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Typography variant='h3'>{props.implementationPhase === true ? 'Implementation Planning Team' : 'Annual Planning Team'}</Typography>
        {!props.readonly &&
          <EditOutlined data-testid='edit-team' sx={{ fill: 'black', cursor: 'pointer', ml: '.5em' }} onClick={() => { setShowEditModal(true) }} />
        }
      </Box>

      {planAdministrators.length > 0 &&
        <>
          <Box sx={{ display: 'grid', justifyContent: 'center', mt: '1em' }}>
            <Typography variant='h5'>Plan Administrators</Typography>
          </Box>
          <Box data-testid='adminwrapper'>
            { planAdministrators.map((member) => (
              <Box key={`${member.userId}admin-labelcontainer`} sx={member.isActive ? activeStyle : inactiveStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid={`user${member.userId}admin-chip`}>
                  {
                    !member.isActive && <Box sx={{ mx: '.5em' }}><InactivePersonIcon/></Box>
                  }
                  <ProfileImage
                    name={`${member.firstName}${member.lastName}image`}
                    imageKey={member.imageKey}
                    backgroundColor={purpleBackground}
                  />
                  <LabelWithToolTip
                    data-testid={`${member.userId}admin--memberlabel`}
                    sx={{ marginLeft: '.5em' }}
                    labelText={`${member.firstName} ${member.lastName} (${member.organizationName})`}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </>
      }

      {planContributors.length > 0 &&
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: '1em' }}>
            <Typography variant='h5'>Plan Contributors</Typography>
          </Box>
          <Box data-testid='contributorwrapper'>
            {planContributors.map((member) => (
              <Box key={`${member.userId}contributor-labelcontainer`} sx={member.isActive ? activeStyle : inactiveStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid={`user${member.userId}contributor-chip`}>
                  {
                    !member.isActive && <Box sx={{ mx: '.5em' }}><InactivePersonIcon/></Box>
                  }
                  <ProfileImage
                    name={`${member.firstName}${member.lastName}image`}
                    imageKey={member.imageKey}
                    backgroundColor={blueBackground}
                  />
                  <LabelWithToolTip
                    data-testid={`${member.userId}contributor--memberlabel`}
                    sx={{ marginLeft: '.5em' }}
                    labelText={`${member.firstName} ${member.lastName} (${member.organizationName})`}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </>
      }

      {planReviewers.length > 0 &&
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: '1em' }}>
            <Typography variant='h5'>Plan Reviewers</Typography>
          </Box>
          <Box data-testid='reviewerwrapper'>
            {planReviewers.map((member) => (
              <Box key={`${member.userId}reviewer-labelcontainer`} sx={{ display: 'flex', alignItems: 'center', my: '1em', ml: '15%', width: '75%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid={`user${member.userId}reviewer-chip`}>
                  <ProfileImage
                    name={`${member.firstName}${member.lastName}image`}
                    imageKey={member.imageKey}
                    backgroundColor={greenBackground}
                  />
                  <LabelWithToolTip
                    data-testid={`${member.userId}reviewer--memberlabel`}
                    sx={{ marginLeft: '.5em' }}
                    labelText={`${member.firstName} ${member.lastName} (${member.organizationName})`}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </>
      }

      {showEditModal &&
        <AnnualPlanningTeamModal closeModal={closeModal} onSubmit={teamSubmit} key='PlanTeamModal' annualPlanId={props.annualPlanId}></AnnualPlanningTeamModal>
      }
    </Card>
  </>
}
